import { useQueries } from '@tanstack/react-query';
import { Anchor, Image, SimpleGrid, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { getReportMedia } from '../../api/resources';
import { mediaGeo, mediaLabel } from '../../lib/reportFields';
import { DetailSection } from './DetailSection';

interface ReportPhotosSectionProps {
    reportIds: string[];
}

/**
 * Fetches media for every report on a visit and renders a single photo grid.
 * Uses `useQueries` to keep all `useQuery` calls at the top level (Rules of
 * Hooks forbids calling hooks inside `.map`).
 */
export function ReportPhotosSection({ reportIds }: ReportPhotosSectionProps) {
    const { t } = useTranslation();

    const mediaResults = useQueries({
        queries: reportIds.map((reportId) => ({
            queryKey: ['report-media', reportId],
            queryFn: () => getReportMedia(reportId),
            enabled: Boolean(reportId),
        })),
    });

    const allMedia = mediaResults.flatMap((q) => q.data?.data ?? []);
    const photos = allMedia.filter((m) => m.downloadUrl);
    const isLoading = mediaResults.some((q) => q.isLoading);

    if (photos.length === 0 && !isLoading) {
        return null;
    }

    return (
        <DetailSection title={t('detail.reportPhotos')}>
            {isLoading && photos.length === 0 ? (
                <Text size="sm" c="dimmed">
                    {t('common.loading.default')}
                </Text>
            ) : (
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
                    {photos.map((m) => {
                        const label = mediaLabel(m);
                        // Photos are GPS-gated at capture, so show where and when
                        // each one was taken — that's the evidence QA reviews.
                        const geo = mediaGeo(m.metadata);
                        const accuracy = m.metadata?.accuracy_m;
                        return (
                            <Stack key={m.id} gap={4}>
                                <Anchor
                                    href={m.downloadUrl ?? undefined}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <Image
                                        src={m.downloadUrl ?? undefined}
                                        alt={label}
                                        radius="md"
                                        h={160}
                                        fit="cover"
                                        bg="gray.1"
                                    />
                                </Anchor>
                                <Text size="xs" fw={600} lineClamp={2}>
                                    {label}
                                </Text>
                                {m.capturedAt ? (
                                    <Text size="xs" c="dimmed">
                                        {new Date(m.capturedAt).toLocaleString()}
                                    </Text>
                                ) : null}
                                {geo.gps ? (
                                    <Anchor
                                        href={geo.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        size="xs"
                                    >
                                        {geo.gps}
                                        {typeof accuracy === 'number' ? ` (±${accuracy}m)` : ''}
                                    </Anchor>
                                ) : null}
                            </Stack>
                        );
                    })}
                </SimpleGrid>
            )}
        </DetailSection>
    );
}
